import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { contactInfo, founder } from "@/data/mock";
import { Reveal, ChapterTag } from "@/lib/motion";
import {
  ArrowRight,
  ExternalLink,
  Heart,
  Target,
  Users,
  Shield,
  Linkedin,
  Award,
  Building2,
  Lightbulb,
  Sprout,
} from "lucide-react";

const values = [
  {
    icon: Heart,
    title: "Impact First",
    description: "Focused on solving real problems that matter — for founders, teams, customers and communities.",
  },
  {
    icon: Target,
    title: "Clarity Over Complexity",
    description: "Cutting through noise and jargon. The job is to make things simpler, not more complicated.",
  },
  {
    icon: Users,
    title: "People-Centred",
    description: "Technology should serve people, not the other way around. Solutions are co-designed with the people who use them.",
  },
  {
    icon: Shield,
    title: "Honest & Practical",
    description: "No over-engineering, no selling what you don't need. Just practical, sustainable improvements that work.",
  },
];

const experienceAreas = [
  "Innovation programmes",
  "Startup & scale-up support",
  "Business analysis",
  "Digital transformation",
  "Operational improvement",
  "Project & programme management",
  "User research & co-design",
  "User acceptance testing",
  "Technology roadmaps",
  "Built environment",
  "Property & housing",
  "Community & social impact",
  "E-commerce",
  "Entrepreneurship",
  "Investment readiness",
  "Product development",
];

const interests = [
  {
    icon: Building2,
    title: "Built Environment",
    description: "Particular experience with housing, property and construction organisations modernising how they operate and serve residents.",
  },
  {
    icon: Sprout,
    title: "Impact-Led Organisations",
    description: "Supporting community organisations, social enterprises and charities to make better use of their time, tools and data.",
  },
  {
    icon: Lightbulb,
    title: "Entrepreneurship & Innovation",
    description: "Helping founders — including those from underrepresented backgrounds — turn ideas into practical, deliverable businesses and products.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About Shakirah Akinwale & Teqie | Business Growth Consultant";
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="about-page">
      {/* 1. Story */}
      <section className="pt-36 pb-20 relative overflow-hidden grain">
        <div className="absolute top-0 right-0 w-[480px] h-[480px] bg-brand-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <ChapterTag num="01" label="The story" />
            <h1 className="mt-6 font-display font-semibold text-stone-900 tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl max-w-3xl" data-testid="about-headline">
              Better business performance first. <em className=" text-brand-700">Technology second.</em>
            </h1>
            <p className="mt-8 text-stone-600 leading-relaxed max-w-2xl text-base md:text-lg">
              Teqie exists because too many capable businesses are harder to run than they should be —
              and too much consulting starts with a solution instead of the problem.
            </p>
            <p className="mt-4 text-stone-600 leading-relaxed max-w-2xl">
              I founded Teqie to work differently: understand how the business actually works today,
              find where time, money, capacity or opportunities are being lost, and then apply the right
              combination of strategy, process improvement, technology, project delivery, coaching or mentoring.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2. Shakirah */}
      <section className="py-24 bg-[#032e2d] relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-start">
            <Reveal>
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-800 to-[#0a4443] aspect-[4/5] flex items-center justify-center border border-teal-100/10" data-testid="about-portrait">
                {founder.photo ? (
                  <img src={founder.photo} alt={founder.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center px-8">
                    <span className="font-display font-normal  text-8xl text-teal-100/80">SA</span>
                    <p className="mt-4 text-xs uppercase tracking-[0.22em] text-teal-200/50">Shakirah Akinwale</p>
                  </div>
                )}
              </div>
              <a
                href={founder.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin-card"
                className="mt-5 flex items-center gap-3 rounded-2xl border border-teal-100/15 px-5 py-4 hover:border-brand-400/50 hover:bg-teal-50/[0.05] transition-all duration-300 group"
              >
                <Linkedin className="w-5 h-5 text-brand-300 flex-shrink-0" />
                <span className="text-sm text-teal-100/80 group-hover:text-white transition-colors duration-200">Connect with Shakirah on LinkedIn</span>
                <ExternalLink className="w-3.5 h-3.5 text-teal-100/50 ml-auto" />
              </a>
            </Reveal>

            <div>
              <Reveal>
                <ChapterTag num="02" label="Multidisciplinary by design" dark />
                <h2 className="mt-6 font-display font-semibold text-white tracking-tight text-3xl sm:text-4xl leading-[1.12]">
                  I'm Shakirah Akinwale, founder of Teqie.
                </h2>
              </Reveal>
              <div className="mt-7 space-y-5">
                {founder.paragraphs.slice(0, 4).map((p, i) => (
                  <Reveal key={i} delay={0.05 * i}>
                    <p className="text-teal-100/75 leading-relaxed">{p}</p>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <p className="mt-7 font-display  text-teal-50/90 text-base md:text-lg leading-relaxed border-l-2 border-[#A86532] pl-5">
                  {founder.transparency}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <ChapterTag num="03" label="Values" />
            <h2 className="mt-6 font-display font-semibold text-stone-900 tracking-tight text-3xl sm:text-4xl">
              What Teqie stands for
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={i} delay={i * 0.07} className="group p-7 rounded-3xl bg-[#FAF7F2] border border-stone-200 hover:border-brand-400/50 hover:-translate-y-1 transition-all duration-300" data-testid={`value-card-${i}`}>
                  <div className="w-11 h-11 rounded-full bg-[#032e2d] flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-teal-100" />
                  </div>
                  <h3 className="text-base md:text-lg font-display font-semibold text-stone-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{v.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Areas of experience */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <ChapterTag num="04" label="Experience" />
            <h2 className="mt-6 font-display font-semibold text-stone-900 tracking-tight text-3xl sm:text-4xl max-w-2xl">
              Areas of experience
            </h2>
            <p className="mt-4 text-stone-500 max-w-2xl leading-relaxed">
              Teqie works across sectors, with particular experience and interest in the built environment,
              entrepreneurship, innovation and impact-led organisations.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="experience-tags">
              {experienceAreas.map((area) => (
                <span key={area} className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-brand-500 hover:text-brand-800 transition-colors duration-200">
                  {area}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {interests.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={i} delay={i * 0.08} className="rounded-3xl bg-white border border-stone-200 p-8 hover:shadow-[0_16px_40px_rgba(3,46,45,0.06)] transition-shadow duration-300" data-testid={`interest-card-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-700" />
                  </div>
                  <h3 className="text-lg font-display font-semibold text-stone-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. Resiax / Products */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <ChapterTag num="05" label="Products" />
            <h2 className="mt-6 font-display font-semibold text-stone-900 tracking-tight text-3xl sm:text-4xl">
              Beyond consulting
            </h2>
            <p className="mt-4 text-stone-500 max-w-2xl leading-relaxed">
              Alongside client work, Teqie builds products that solve real problems — and helps clients build theirs.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 rounded-3xl bg-[#FAF7F2] border border-stone-200 p-8 sm:p-10 max-w-2xl" data-testid="resiax-card">
              <div className="flex items-start gap-4 mb-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_teqie-new/artifacts/qbnokejv_RESI.png"
                  alt="Resiax"
                  className="w-14 h-14 rounded-2xl object-contain flex-shrink-0"
                />
                <div>
                  <h3 className="text-xl font-display font-semibold text-stone-900">Resiax</h3>
                  <p className="text-sm text-stone-500">Teqie's flagship product</p>
                </div>
              </div>
              <p className="text-stone-600 leading-relaxed mb-6">
                Resiax is Teqie's dedicated platform built to tackle real challenges in the built environment.
                Visit the site to learn more about how it's making a difference.
              </p>
              <a
                href="https://resiax.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="resiax-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#A86532] hover:text-[#8B4D24] transition-colors duration-200 link-underline"
              >
                Visit Resiax.com
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 bg-[#032e2d] relative grain">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <h2 className="font-display font-semibold text-white tracking-tight text-3xl sm:text-4xl leading-[1.1]">
              Let's start with a <em className=" text-brand-300">conversation</em>.
            </h2>
            <p className="mt-5 text-teal-100/75 leading-relaxed">
              If something in the business feels harder, slower or more expensive than it should,
              you don't need to know the solution before we speak.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer")}
                data-testid="about-book-cta"
                className="bg-teal-50 hover:bg-white text-[#032e2d] rounded-full h-12 px-8 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
              >
                Book a Growth Conversation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
